import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { AuthenticationService } from '../services/authentication.service';
import { TrackingService } from '../services/tracking.service';

export const CanActivateAuth: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthenticationService);
  const trackingService = inject(TrackingService);
  const router = inject(Router);

  return AuthGuard.canActivate(route, state, authService, trackingService, router);
};

export class AuthGuard {
  private static authQueryMappings = [
    {
      input: 'src',
      output: 'referrerCode',
      // Match anything except empt
      pattern: /^(.+)$/,
      replacement: 'src_$1',
      strip: false,
    },
    {
      input: 'cid',
      output: 'referrerCode',
      // Match anything except empt
      pattern: /^.+$/,
      strip: true,
    },
    {
      input: 'sig',
      output: 'sig',
      // Match anything except empt
      pattern: /^.+$/,
      strip: true,
    },
    {
      input: 'acr_values',
      output: 'acr_values',
      // Match anything except empt
      pattern: /^.+$/,
      strip: true,
    },
  ];

  static async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    authService: AuthenticationService,
    trackingService: TrackingService,
    router: Router,
  ) {
    if (await authService.isAuthenticatedAnywhere()) {
      const url = location.href.split(location.host)[1];
      const matchSrc = url && /[&?]src=([^&#]+)/.exec(url);
      if (matchSrc) {
        trackingService.trackMarketingSource(matchSrc[1]);
        if (route.queryParams.src) {
          await router.navigateByUrl(AuthGuard.stripParameterFromUrl(url, 'src'), {
            replaceUrl: true,
          });
        }
      }

      return true;
    }

    const queryState = route.queryParams.state;
    if (queryState) {
      const stateOrigin = queryState.split(/\b\/\b/)[0];

      if (stateOrigin.toLowerCase() !== location.origin.toLowerCase()) {
        throw new Error(`Unexpected origin: ${stateOrigin}`);
      }

      setTimeout(() => (location.href = queryState));
      return false;
    }

    const { returnUrl, customQueryParams } = AuthGuard.getReturnUrlAndCustomQueryParams(state.url);

    authService.init(returnUrl, customQueryParams);

    return false;
  }

  static stripParameterFromUrl(url: string, parameter: string) {
    return url.replace(new RegExp(`([&?])${parameter}=[^&#]*&?`), '$1').replace(/[?&]$/, '');
  }

  static getReturnUrlAndCustomQueryParams(url: string) {
    let returnUrl = url;

    const queryParams = Object.fromEntries(
      (url.split(/\?/)[1] ?? '').split(/&/g).map(x => x.split(/=/)),
    );

    const customQueryParams: { [key: string]: string } = {};

    for (const mapping of AuthGuard.authQueryMappings) {
      const value = queryParams[mapping.input];
      if (value && mapping.pattern.test(value)) {
        customQueryParams[mapping.output] = mapping.replacement
          ? value.replace(mapping.pattern, mapping.replacement)
          : value;
        if (mapping.strip) {
          returnUrl = AuthGuard.stripParameterFromUrl(returnUrl, mapping.input);
        }
      }
    }

    return { returnUrl, customQueryParams };
  }
}
